import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import type { TeamMemberData } from '../../../types/n8nResponses';
import { getMemberInitials } from './AdminPrimitives';

/** True only for a real http(s) URL — filters stored "null"/"" junk that
 *  would render as a broken-image icon. */
const isUsableImageUrl = (value: unknown): value is string =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim()) && value.trim().toLowerCase() !== 'null';

/** Avatar span: picture when usable + loaded, initials on absence or error. */
const MemberAvatarImage: React.FC<{
  name?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  className: string;
}> = ({ name, email, imageUrl, className }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [imageUrl]);
  const usable = isUsableImageUrl(imageUrl) && !failed;
  return (
    <span className={className}>
      {usable
        ? <img src={(imageUrl as string).trim()} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
        : getMemberInitials(name, email)}
    </span>
  );
};

const VIEWPORT_PADDING = 12;
const MENU_MAX_HEIGHT = 320;

export interface MemberSelectProps {
  id: string;
  /** Selected member's Appwrite user id ('' when none). */
  value: string;
  onChange: (userId: string) => void;
  members: TeamMemberData[];
  /** aria-label / listbox label. */
  label: string;
  placeholder?: string;
  disabled?: boolean;
  /** Extra classes on the trigger button (lets callers match card styling). */
  triggerClassName?: string;
}

/**
 * MemberSelect — portal dropdown listing team members as AVATAR + NAME rows.
 * Native <option> elements can't render images, so this mirrors the GeoSelect
 * portal pattern: trigger button → floating menu with picture-per-option
 * (profile picture with initials fallback), keyboard navigation, search.
 */
export const MemberSelect: React.FC<MemberSelectProps> = ({
  id,
  value,
  onChange,
  members,
  label,
  placeholder = 'Select member…',
  disabled = false,
  triggerClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number; width: number; maxHeight: number; placement: 'top' | 'bottom' } | null>(null);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef(new Map<string, HTMLButtonElement>());

  const menuId = `${id}-menu`;
  const selected = useMemo(
    () => members.find((member) => member.user_id === value) || null,
    [members, value],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((member) =>
      (member.name || '').toLowerCase().includes(q) || member.email.toLowerCase().includes(q));
  }, [members, search]);

  const updateMenuPosition = useCallback(() => {
    if (typeof window === 'undefined' || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const availableWidth = window.innerWidth - VIEWPORT_PADDING * 2;
    const width = Math.min(Math.max(rect.width, 280), availableWidth);
    const left = Math.min(Math.max(rect.left, VIEWPORT_PADDING), window.innerWidth - width - VIEWPORT_PADDING);
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const placement = spaceBelow < 240 && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const availableHeight = placement === 'top' ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(180, Math.min(MENU_MAX_HEIGHT, availableHeight - 8));
    setMenuPosition({ left, top: placement === 'top' ? rect.top - 8 : rect.bottom + 8, width, maxHeight, placement });
  }, []);

  const closeMenu = useCallback((restoreFocus = false) => {
    setOpen(false);
    setSearch('');
    if (restoreFocus) window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) closeMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(true);
      }
    };
    updateMenuPosition();
    const positionFrame = window.requestAnimationFrame(updateMenuPosition);
    const focusFrame = window.requestAnimationFrame(() => searchRef.current?.focus());
    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.cancelAnimationFrame(positionFrame);
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [closeMenu, open, updateMenuPosition]);

  const moveOptionFocus = (currentIndex: number, direction: 1 | -1) => {
    const count = filtered.length;
    if (count === 0) return;
    const nextIndex = (currentIndex + direction + count) % count;
    optionRefs.current.get(filtered[nextIndex].user_id)?.focus();
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveOptionFocus(index, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveOptionFocus(index, -1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onChange(filtered[index].user_id);
      closeMenu(true);
    } else if (event.key === 'Tab') {
      closeMenu();
    }
  };

  const assignOptionRef = (userId: string) => (element: HTMLButtonElement | null) => {
    if (element) optionRefs.current.set(userId, element);
    else optionRefs.current.delete(userId);
  };

  const menu = open && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="listbox"
          aria-label={label}
          style={menuPosition
            ? { left: menuPosition.left, top: menuPosition.top, width: menuPosition.width, maxHeight: menuPosition.maxHeight }
            : { visibility: 'hidden' }}
          className="fixed z-[10040] flex flex-col overflow-hidden rounded-2xl border border-white/12 bg-[#101217]/[0.985] shadow-[0_24px_64px_rgba(0,0,0,0.58),inset_0_1px_0_rgba(255,255,255,0.045)] backdrop-blur-2xl"
        >
          <div className="h-px bg-gradient-to-r from-transparent via-cyan-300/45 to-transparent" />
          <div className="border-b border-white/[0.07] p-2">
            <div className="relative">
              <Icon icon="bx:search" className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search members…"
                className="w-full rounded-xl border border-white/8 bg-white/[0.03] py-2 pl-8 pr-3 text-sm text-white placeholder:text-gray-600 focus:border-cyan-300/25 focus:outline-none focus:ring-1 focus:ring-cyan-300/20"
              />
            </div>
          </div>
          <div className="space-y-0.5 overflow-y-auto p-1.5" style={{ maxHeight: menuPosition ? Math.max(100, menuPosition.maxHeight - 100) : 240 }}>
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-gray-500">No members found.</div>
            )}
            {filtered.map((member, index) => {
              const isSelected = member.user_id === value;
              return (
                <button
                  ref={assignOptionRef(member.user_id)}
                  key={member.user_id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => { onChange(member.user_id); closeMenu(true); }}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                  className={`flex w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/25 ${
                    isSelected
                      ? 'border-cyan-300/20 bg-cyan-300/[0.08] text-white'
                      : 'border-transparent text-white/72 hover:border-white/[0.06] hover:bg-white/[0.045] hover:text-white'
                  }`}
                >
                  {/* Avatar: profile picture with initials fallback (shared rule) */}
                  <MemberAvatarImage
                    name={member.name}
                    email={member.email}
                    imageUrl={member.profile_picture_url}
                    className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-[#181b24] text-[9px] font-bold text-[#a3aed3]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{member.name || member.email}</span>
                    <span className="block truncate text-[10px] text-white/40">{member.email}</span>
                  </span>
                  <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors ${
                    isSelected ? 'border-cyan-300/45 bg-cyan-300/15 text-cyan-100' : 'border-white/12 text-transparent'
                  }`}>
                    <Icon icon="bx:check" className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="w-full">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        // stopPropagation inside the handler itself — the wrapper div's React
        // onClick stopper runs on the parent (bubble AFTER this), but card
        // surfaces also attach native listeners; keep the toggle atomic here.
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        className={`group relative flex w-full items-center gap-2 rounded-[12px] border text-left transition-all focus:outline-none focus:ring-2 focus:ring-cyan-300/10 disabled:cursor-not-allowed disabled:opacity-60 ${triggerClassName}`}
      >
        <MemberAvatarImage
          name={selected?.name}
          email={selected?.email}
          imageUrl={selected?.profile_picture_url}
          className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-[#181b24] text-[7px] font-bold text-[#a3aed3]"
        />
        <span className={`min-w-0 flex-1 truncate text-[10px] font-bold leading-none ${selected ? 'text-current' : 'text-white/55'}`}>
          {selected ? (selected.name || selected.email) : placeholder}
        </span>
        <Icon icon="bx:chevron-down" className="pointer-events-none h-3.5 w-3.5 shrink-0 text-white/45" aria-hidden="true" />
      </button>
      {menu}
    </div>
  );
};
